import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Platform, type TextStyle, type ViewStyle } from 'react-native';
import moment, { type Moment } from 'moment';
import { BottomSheetPopup } from './BottomSheetPopup';
import { Clickable } from './Clickable';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { androidFlatSurfaceStyle, androidScaleSize } from '../../shared/theme/androidUiParity';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../../shared/theme/sharedStyles';
import { createSheetFooterDividerStyle } from '../../shared/theme/styles/sheets';
import { DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import { useTranslation, formatNumber, getLocalizedWeekdaysShort } from '../../shared/i18n';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { formatMomentForApiYmd } from '../../shared/utils/dateUtils';
import { CalendarMonthNavRow, CalendarWeekdayStrip } from '../ui';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const DAY_CELL_MIN_HEIGHT = 34;
const FOOTER_BTN_HEIGHT = androidScaleSize(48);

type CalendarDay = {
  date: string;
  day_of_month: number;
  is_disabled: boolean;
  is_blocked: boolean;
  is_padding: boolean;
};

export type SelectionCalendarSheetProps = {
  visible: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  selected_date: Moment;
  on_save: (date: Moment) => void;
  is_date_selectable: (date_str: string) => boolean;
  is_date_blocked?: (date_str: string) => boolean;
  min_month?: Moment;
  max_month?: Moment;
  /** Rendered after subtitle and before month navigation (e.g. checkout weekday off picker). */
  header_content?: React.ReactNode;
  /** When selectable rules change, nudge an invalid draft date to the next valid day. */
  repair_selected_date?: (date: Moment) => Moment;
};

function create_styles(c: ThemeColors) {
  const instruction_bg = (hexToRgba(c.primary, 0.08) as string) || c.primaryUltra;
  const blocked_swatch = (hexToRgba(c.alert, 0.35) as string) || c.alertFade;

  return StyleSheet.create({
    sheet: {
      backgroundColor: c.surface,
      maxHeight: '88%',
      borderTopLeftRadius: DASH_STUDIO_RADIUS,
      borderTopRightRadius: DASH_STUDIO_RADIUS,
      overflow: 'hidden' as const,
    },
    instruction_card: {
      marginHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.lg,
      backgroundColor: instruction_bg,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: (hexToRgba(c.primary, 0.22) as string) || c.border,
    },
    instruction_text: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      lineHeight: 18,
      ...ANDROID_TEXT_COMPACT,
    },
    month_nav_wrap: {
      paddingHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.xs,
    },
    week_days_row: {
      flexDirection: 'row',
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.xs,
      marginHorizontal: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.md,
      backgroundColor: (hexToRgba(c.text, 0.04) as string) || c.surfaceLight,
      ...horizontalLtrDirectionStyle,
    },
    week_day_cell: {
      flex: 1,
      alignItems: 'center',
    },
    week_day_text: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      fontWeight: '800',
      letterSpacing: 0.3,
      ...ANDROID_TEXT_COMPACT,
    },
    calendar_grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.xs,
      paddingBottom: appTheme.spacing.md,
      marginHorizontal: appTheme.spacing.sm,
      ...horizontalLtrDirectionStyle,
    },
    day_cell_wrap: {
      width: '14.28%',
      aspectRatio: 1,
      minHeight: DAY_CELL_MIN_HEIGHT,
    },
    day_cell: {
      width: '100%',
      aspectRatio: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 2,
    },
    day_cell_disabled: {
      opacity: 0.38,
    },
    day_cell_selected: {
      backgroundColor: c.primary,
      borderRadius: appTheme.borderRadius.pill,
    },
    day_cell_blocked: {
      backgroundColor: blocked_swatch,
      borderRadius: appTheme.borderRadius.pill,
    },
    day_text: {
      fontSize: 14,
      fontWeight: '600',
      color: c.text,
      textAlign: 'center',
      ...ANDROID_TEXT_COMPACT,
    },
    day_text_disabled: {
      color: c.textLight,
      fontWeight: '500',
    },
    day_text_selected: {
      color: c.onPrimary ?? c.white,
      fontWeight: '700',
    },
    footer: {
      paddingHorizontal: appTheme.spacing.lg,
      paddingBottom: appTheme.spacing.xs,
      backgroundColor: c.surface,
      ...createSheetFooterDividerStyle(c),
    },
    footer_row: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: appTheme.spacing.sm,
    },
    footer_grow: {
      flex: 1,
      minWidth: 0,
    },
    footer_btn_shell: {
      width: '100%',
      minHeight: FOOTER_BTN_HEIGHT,
      borderRadius: appTheme.borderRadius.lg,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.sm,
      ...Platform.select({
        ios: {
          shadowColor: 'transparent',
          shadowOpacity: 0,
          shadowRadius: 0,
          shadowOffset: { width: 0, height: 0 },
        },
        android: androidFlatSurfaceStyle(),
      }),
    } satisfies ViewStyle,
    confirm_btn_disabled: {
      backgroundColor: (hexToRgba(c.primary, 0.28) as string) || c.primaryFade,
    },
    confirm_label_disabled: {
      color: c.textSecondary,
    },
  });
}

const DayCell = React.memo(function DayCell({
  day,
  is_selected,
  on_press,
  styles,
}: {
  day: CalendarDay;
  is_selected: boolean;
  on_press: (day: CalendarDay) => void;
  styles: ReturnType<typeof create_styles>;
}) {
  const handle_press = useCallback(() => {
    on_press(day);
  }, [day, on_press]);

  if (day.is_padding) {
    return <View style={styles.day_cell_wrap} pointerEvents="none" />;
  }

  const show_blocked = day.is_blocked && !is_selected && !day.is_disabled;

  return (
    <Clickable
      style={styles.day_cell_wrap}
      onPress={handle_press}
      disabled={day.is_disabled}
      accessibilityLabel={formatNumber(day.day_of_month)}
      accessibilityRole="button">
      <View
        style={[
          styles.day_cell,
          day.is_disabled && styles.day_cell_disabled,
          is_selected && styles.day_cell_selected,
          show_blocked && styles.day_cell_blocked,
        ]}>
        <Text
          style={[
            styles.day_text,
            day.is_disabled && styles.day_text_disabled,
            is_selected && styles.day_text_selected,
          ]}>
          {formatNumber(day.day_of_month)}
        </Text>
      </View>
    </Clickable>
  );
});

export function SelectionCalendarSheet({
  visible,
  onClose,
  title,
  subtitle,
  selected_date,
  on_save,
  is_date_selectable,
  is_date_blocked,
  min_month,
  max_month,
  header_content,
  repair_selected_date,
}: SelectionCalendarSheetProps) {
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => create_styles(colors), [colors]);
  const { t, locale } = useTranslation();
  const week_days = useMemo(() => getLocalizedWeekdaysShort(locale ?? 'en'), [locale]);
  const primary_cta = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(colors),
      label: createStackedFooterPrimaryLabelStyle(colors),
    }),
    [colors],
  );
  const secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(colors),
      label: createStackedFooterSecondaryLabelStyle(colors),
    }),
    [colors],
  );

  const [current_month, set_current_month] = useState(() => selected_date.clone().startOf('month'));
  const [draft_date, set_draft_date] = useState(() => selected_date.clone().startOf('day'));

  useEffect(() => {
    if (!visible) {
      return;
    }
    const next = selected_date.clone().startOf('day');
    const repaired = repair_selected_date ? repair_selected_date(next) : next;
    set_draft_date(repaired);
    set_current_month(repaired.clone().startOf('month'));
  }, [visible, selected_date]);

  useEffect(() => {
    if (!visible || !repair_selected_date) {
      return;
    }
    set_draft_date(prev => {
      const repaired = repair_selected_date(prev);
      return repaired.isSame(prev, 'day') ? prev : repaired;
    });
  }, [visible, repair_selected_date]);

  const calendar_days = useMemo(() => {
    const days: CalendarDay[] = [];
    const start_of_month = current_month.clone().startOf('month');
    const end_of_month = current_month.clone().endOf('month');
    const first_day_of_week = start_of_month.day();

    for (let i = 0; i < first_day_of_week; i++) {
      days.push({
        date: '',
        day_of_month: 0,
        is_disabled: true,
        is_blocked: false,
        is_padding: true,
      });
    }

    let day = start_of_month.clone();
    while (day.isSameOrBefore(end_of_month, 'day')) {
      const date_str = formatMomentForApiYmd(day);
      const selectable = is_date_selectable(date_str);
      const blocked = is_date_blocked?.(date_str) ?? false;
      days.push({
        date: date_str,
        day_of_month: day.date(),
        is_disabled: !selectable,
        is_blocked: blocked,
        is_padding: false,
      });
      day.add(1, 'day');
    }

    return days;
  }, [current_month, is_date_selectable, is_date_blocked]);

  const month_floor = min_month?.clone().startOf('month') ?? moment().startOf('month');
  const month_ceiling = max_month?.clone().startOf('month') ?? moment().add(2, 'years').startOf('month');

  const can_navigate_prev = useMemo(
    () => current_month.clone().subtract(1, 'month').isSameOrAfter(month_floor, 'month'),
    [current_month, month_floor],
  );

  const can_navigate_next = useMemo(
    () => current_month.clone().add(1, 'month').isSameOrBefore(month_ceiling, 'month'),
    [current_month, month_ceiling],
  );

  const draft_key = formatMomentForApiYmd(draft_date);
  const can_save = is_date_selectable(draft_key);

  const handle_day_press = useCallback(
    (day: CalendarDay) => {
      if (day.is_disabled || day.is_padding) {
        return;
      }
      set_draft_date(moment(day.date, 'YYYY-MM-DD').startOf('day'));
    },
    [],
  );

  const handle_save = useCallback(() => {
    if (!can_save) {
      return;
    }
    on_save(draft_date.clone());
    onClose();
  }, [can_save, draft_date, on_save, onClose]);

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={onClose}
      title={title}
      animationType="fade"
      showDragHandle
      sheetHeightMode="fitContent"
      sheetStyle={styles.sheet}>
      {subtitle ? (
        <View style={styles.instruction_card}>
          <Text style={styles.instruction_text}>{subtitle}</Text>
        </View>
      ) : null}

      {header_content}

      <View style={styles.month_nav_wrap}>
        <CalendarMonthNavRow
          currentMonth={current_month}
          canNavigatePrev={can_navigate_prev}
          canNavigateNext={can_navigate_next}
          onPrevMonth={() => can_navigate_prev && set_current_month(prev => prev.clone().subtract(1, 'month'))}
          onNextMonth={() => can_navigate_next && set_current_month(prev => prev.clone().add(1, 'month'))}
          previousMonthLabel={t('calendar.previousMonth')}
          nextMonthLabel={t('calendar.nextMonth')}
        />
      </View>

      <CalendarWeekdayStrip
        labels={week_days}
        rowStyle={styles.week_days_row}
        cellStyle={styles.week_day_cell}
        textStyle={styles.week_day_text}
      />

      <View style={styles.calendar_grid}>
        {calendar_days.map((day, index) => (
          <DayCell
            key={day.is_padding ? `pad-${index}` : day.date}
            day={day}
            is_selected={!day.is_padding && day.date === draft_key}
            on_press={handle_day_press}
            styles={styles}
          />
        ))}
      </View>

      <View style={styles.footer}>
        <View style={styles.footer_row}>
          <View style={styles.footer_grow}>
            <Clickable onPress={onClose} accessibilityLabel={t('common.cancel')} accessibilityRole="button">
              <View style={[secondary_cta.surface, styles.footer_btn_shell]}>
                <Text style={secondary_cta.label}>{t('common.cancel')}</Text>
              </View>
            </Clickable>
          </View>
          <View style={styles.footer_grow}>
            <Clickable
              onPress={handle_save}
              disabled={!can_save}
              accessibilityLabel={t('common.save')}
              accessibilityRole="button">
              <View style={[primary_cta.surface, styles.footer_btn_shell, !can_save && styles.confirm_btn_disabled]}>
                <Text style={[primary_cta.label, !can_save && styles.confirm_label_disabled]}>{t('common.save')}</Text>
              </View>
            </Clickable>
          </View>
        </View>
      </View>
    </BottomSheetPopup>
  );
}

export default SelectionCalendarSheet;
